/**
 * InspectionSeries/Index mount point.
 *
 * Thin compatibility export consumed by the Rails ERB mounts (task-11):
 *
 *   Standalone — `app/views/inspection_series/listing.html.erb`
 *   Renders WorkspaceHeader with title and optional New Inspection Series action.
 *   showHeader: true, pageTitle from t('.title'), newSeriesUrl/canCreateSeries/newSeriesLabel
 *   from policy. No cacheKey prop — standalone scope handled by cacheKeyForScope.
 *
 *   Building context — `app/views/buildings/tabs/_inspection_series.html.erb`
 *   Flag-ON: showHeader true so the listing owns WorkspaceHeader and New Series.
 *   buildingId passed; scope resolves to { type: 'building', id: buildingId }.
 *
 *   Account context — `app/views/accounts/menu/_inspection_series_tab.html.erb`
 *   Flag-ON: showHeader true (same WorkspaceHeader treatment).
 *   accountId passed; scope resolves to { type: 'account', id: accountId }.
 *
 * The module-level QueryClient is stable across re-renders. Each ERB mount
 * gets its own isolated cache via scope (buildingId / accountId).
 *
 * Replaces the legacy jQuery Kendo Grid implementation. Dead props (cacheKey,
 * defaultHiddenColumns, nonHidableColumns) are removed from ERB mounts; those
 * concerns are handled internally by columnPolicyForScope / cacheKeyForScope.
 *
 * Flag-aware: when modernizedInspectionListingsEnabled is false (default), renders
 * the legacy Kendo-based listing. When true, renders the modern React Query listing.
 * The flag prop is stripped before forwarding so neither branch receives it.
 */

/* eslint-disable react/prop-types */
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { InspectionSeriesListing } from './InspectionSeriesListing.tsx';
import LegacyInspectionSeriesIndex from './legacy/LegacyInspectionSeriesIndex';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

export default function InspectionSeriesIndex({ modernizedInspectionListingsEnabled = false, ...props }) {
  if (!modernizedInspectionListingsEnabled) {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <LegacyInspectionSeriesIndex {...props} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <InspectionSeriesListing {...props} />
    </QueryClientProvider>
  );
}
